import { diffDaysIso, isoToRu, ruToIso, tomorrowIso } from '@/lib/date';
import { TONS_PER_VEHICLE } from '@/lib/logistics';
import type { LoadingConditions, SaleOfferDraft, SaleOrder, UploadedFile } from '@/types/sell';

// Лимиты (сверены со старым порталом sell-offer-*-input).
export const WEIGHT_MIN = 20;
export const PRICE_MIN = 1000;
export const VEHICLE_HEIGHT_MIN = 4;
export const VEHICLE_HEIGHT_MAX = 10;
export const RATE_TON_MIN = 20;
export const RATE_TS_MIN = 1;
export const RATE_MAX = 999_999;
export const SCALES_MIN = 1;
export const SCALES_MAX = 50;
export const STORAGE_PRICE_MIN = 1;
export const DECLARATION_NO_MAX = 100;
export const COMMENT_MAX = 4096;

export type StartDateMode = 'NO_DATE' | 'EXACT_DATE' | 'N_DAYS_AFTER_PAYMENT';
export type RateType = 'ton' | 'TS';

/** Базис (точка погрузки) — минимум для сборки запроса. */
export interface SellAddress {
  readonly address: string;
  readonly title?: string;
  readonly basisId?: number;
}

/** Плоское состояние формы предложения (одна культура + один лот). Даты — ISO «yyyy-mm-dd». */
export interface SellFormState {
  goodsType: string | null; // класс-разделённая культура («Пшеница 3 класс»)
  propsSetId: string | null;
  cropYear: number;
  weightTon: number | '';
  toExchange: boolean;
  price: number | '';
  prepayment: string | null;
  qualityCreditType: string | null;
  startMode: StartDateMode;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  shipmentModes: string[];
  trailers: boolean;
  trawls: boolean;
  hasScales: boolean;
  scalesLength: number | '';
  vehicleHeight: number | '';
  rateType: RateType;
  rate: number | '';
  storageAvailable: boolean;
  storagePrice: number | '';
  declarationNo: string;
  declarationDate: string;
  declarationFiles: UploadedFile[];
  extraConditions: boolean;
  specialTermsFiles: UploadedFile[];
  userComment: string;
  agreeSupply: boolean;
  agreeOffer: boolean;
}

export function initialSellForm(): SellFormState {
  return {
    goodsType: null,
    propsSetId: null,
    cropYear: new Date().getFullYear(),
    weightTon: '',
    toExchange: false,
    price: '',
    prepayment: 'PERCENT_0',
    qualityCreditType: 'BY_AVERAGE_VALUE',
    startMode: 'NO_DATE',
    startDate: '',
    endDate: '',
    startTime: '08:00',
    endTime: '18:00',
    shipmentModes: [],
    trailers: true,
    trawls: true,
    // Весы включены по умолчанию (как тралы/самосвалы) — так расчёт лучшей цены и дефолты оффера
    // консистентны (всё оборудование ВКЛ → без надбавок). Длину весов вводят вручную (валидация её
    // требует при включённых весах): дефолтная длина скрыла бы ошибку и ушла бы в заявку неверной.
    hasScales: true,
    scalesLength: '',
    vehicleHeight: VEHICLE_HEIGHT_MIN,
    rateType: 'ton',
    rate: '',
    storageAvailable: false,
    storagePrice: '',
    declarationNo: '',
    declarationDate: '',
    declarationFiles: [],
    extraConditions: false,
    specialTermsFiles: [],
    userComment: '',
    agreeSupply: false,
    agreeOffer: false,
  };
}

const isNum = (v: number | ''): v is number => v !== '' && Number.isFinite(v);

/** Ошибки дат погрузки (ключи i18n); пусто — ошибок нет. Считается только для EXACT_DATE. */
export function loadingDateErrors(state: SellFormState): { startDate?: string; endDate?: string } {
  if (state.startMode !== 'EXACT_DATE') {
    return {};
  }
  const errors: { startDate?: string; endDate?: string } = {};
  if (state.startDate && state.startDate < tomorrowIso()) {
    errors.startDate = 'app.sell.form.loading.errStartPast';
  }
  if (state.startDate && state.endDate && diffDaysIso(state.startDate, state.endDate) < 0) {
    errors.endDate = 'app.sell.form.loading.errEndBeforeStart';
  }
  // Мощность отгрузки vs срок: (end−start) дней должно хватить на объём.
  if (!errors.endDate && state.startDate && state.endDate && isNum(state.weightTon) && isNum(state.rate)) {
    const days = diffDaysIso(state.startDate, state.endDate);
    const tonsPerDay = state.rateType === 'TS' ? state.rate * TONS_PER_VEHICLE : state.rate;
    const minDays = Math.ceil(state.weightTon / tonsPerDay);
    if (days >= 0 && days < minDays) {
      errors.startDate = 'app.sell.form.loading.errCapacity';
      errors.endDate = 'app.sell.form.loading.errCapacity';
    }
  }
  return errors;
}

/** Ограничения объёма биржевого лота (для валидации формы). */
export interface ExchangeLotLimits {
  readonly lotSizeTonnes: number;
  readonly minTonnes: number;
  readonly maxTonnes: number;
}

/**
 * Ошибка объёма биржевого лота (ключ i18n) при `toExchange`: объём кратен лоту и в границах НТБ. `null` —
 * ошибок нет, оффер не биржевой, ограничения ещё не загружены или объём не задан (эти случаи ловят прочие проверки).
 */
export function exchangeLotError(state: SellFormState, limits: ExchangeLotLimits | null | undefined): string | null {
  if (!state.toExchange || !limits || !isNum(state.weightTon)) {
    return null;
  }
  if (state.weightTon < limits.minTonnes) {
    return 'app.sell.exchange.lotMin';
  }
  if (state.weightTon > limits.maxTonnes) {
    return 'app.sell.exchange.lotMax';
  }
  if (state.weightTon % limits.lotSizeTonnes !== 0) {
    return 'app.sell.exchange.lotStep';
  }
  return null;
}

/** Поле формы предложения, которое может мешать отправке. По нему строится сводка и переводится фокус. */
export type SellField =
  | 'address'
  | 'goods'
  | 'quality'
  | 'weight'
  | 'price'
  | 'prepayment'
  | 'credit'
  | 'startDate'
  | 'endDate'
  | 'shipmentModes'
  | 'rate'
  | 'vehicleHeight'
  | 'scalesLength'
  | 'storagePrice'
  | 'declarationNo'
  | 'declarationDate'
  | 'declarationFiles'
  | 'specialFiles';

/** Почему поле мешает отправке: пусто, значение вне допустимого диапазона или ошибка дат (текст — в `dateErrors`). */
export type SellFieldReason = 'required' | 'range' | 'date';

export interface SellFieldProblem {
  readonly field: SellField;
  readonly reason: SellFieldReason;
}

/** Допустимый диапазон числового поля. Форма не подменяет введённое значение, а показывает ошибку диапазона. */
export interface NumberRange {
  readonly min: number;
  readonly max: number;
}

export const WEIGHT_RANGE: NumberRange = { min: WEIGHT_MIN, max: 999_999 };
export const PRICE_RANGE: NumberRange = { min: PRICE_MIN, max: 999_999 };
export const VEHICLE_HEIGHT_RANGE: NumberRange = { min: VEHICLE_HEIGHT_MIN, max: VEHICLE_HEIGHT_MAX };
export const SCALES_RANGE: NumberRange = { min: SCALES_MIN, max: SCALES_MAX };
export const STORAGE_PRICE_RANGE: NumberRange = { min: STORAGE_PRICE_MIN, max: RATE_MAX };

/** Диапазон суточной отгрузки зависит от единицы: в тоннах или в машинах. */
export function rateRange(rateType: RateType): NumberRange {
  return { min: rateType === 'TS' ? RATE_TS_MIN : RATE_TON_MIN, max: RATE_MAX };
}

/** Проблема числового поля: пусто — required, вне диапазона — range, иначе null. */
export function numberProblem(value: number | '', range: NumberRange): SellFieldReason | null {
  if (!isNum(value)) {
    return 'required';
  }
  return value < range.min || value > range.max ? 'range' : null;
}

/** Проблемы условий погрузки (общие для формы предложения и сделки из поиска без заявки), в порядке полей на экране. */
export function logisticsProblems(state: SellFormState): SellFieldProblem[] {
  const problems: SellFieldProblem[] = [];
  const push = (field: SellField, reason: SellFieldReason | null): void => {
    if (reason) {
      problems.push({ field, reason });
    }
  };
  const dates = loadingDateErrors(state);
  if (state.startMode === 'EXACT_DATE') {
    push('startDate', !state.startDate ? 'required' : dates.startDate ? 'date' : null);
    push('endDate', dates.endDate ? 'date' : null);
  }
  push('shipmentModes', state.shipmentModes.length === 0 ? 'required' : null);
  push('rate', numberProblem(state.rate, rateRange(state.rateType)));
  push('vehicleHeight', numberProblem(state.vehicleHeight, VEHICLE_HEIGHT_RANGE));
  if (state.hasScales) {
    push('scalesLength', numberProblem(state.scalesLength, SCALES_RANGE));
  }
  if (state.storageAvailable) {
    push('storagePrice', numberProblem(state.storagePrice, STORAGE_PRICE_RANGE));
  }
  return problems;
}

/** Проблемы полей предложения в порядке на экране; пусто — поля заполнены верно (поручение и согласия — отдельно). */
export function offerProblems(state: SellFormState, address: SellAddress | null): SellFieldProblem[] {
  const problems: SellFieldProblem[] = [];
  const push = (field: SellField, reason: SellFieldReason | null): void => {
    if (reason) {
      problems.push({ field, reason });
    }
  };
  push('address', address ? null : 'required');
  push('goods', state.goodsType ? null : 'required');
  push('quality', state.propsSetId ? null : 'required');
  push('weight', numberProblem(state.weightTon, WEIGHT_RANGE));
  push('price', numberProblem(state.price, PRICE_RANGE));
  push('prepayment', state.prepayment ? null : 'required');
  push('credit', state.qualityCreditType ? null : 'required');
  problems.push(...logisticsProblems(state));
  push('declarationNo', state.declarationNo.trim() ? null : 'required');
  push('declarationDate', state.declarationDate ? null : 'required');
  push('declarationFiles', state.declarationFiles.length > 0 ? null : 'required');
  if (state.extraConditions) {
    push('specialFiles', state.specialTermsFiles.length > 0 ? null : 'required');
  }
  return problems;
}

/** Заполнены ли ВСЕ обязательные поля и нет ошибок дат (готовность к генерации поручения/сабмиту). */
export function isOfferComplete(state: SellFormState, address: SellAddress | null): boolean {
  return offerProblems(state, address).length === 0;
}

/** Готовность ЛОГИСТИКИ продавца (для сделки из поиска без заявки): объём и условия погрузки, без полей предложения
 * (культура, цена, декларация). Предоплата, поручение и лицензия проверяются отдельно. */
export function isDealLogisticsComplete(state: SellFormState): boolean {
  return numberProblem(state.weightTon, WEIGHT_RANGE) == null && logisticsProblems(state).length === 0;
}

export function buildLoadingConditions(state: SellFormState): LoadingConditions {
  return {
    startDateMode: state.startMode,
    startDate: state.startMode === 'EXACT_DATE' ? (isoToRu(state.startDate) ?? undefined) : undefined,
    endDate: state.startMode === 'EXACT_DATE' && state.endDate ? (isoToRu(state.endDate) ?? undefined) : undefined,
    startTime: state.startTime,
    endTime: state.endTime,
    shipmentModes: state.shipmentModes,
    dailyShippingRate: { value: isNum(state.rate) ? state.rate : 0, type: state.rateType },
    maxVehicleHight: isNum(state.vehicleHeight) ? state.vehicleHeight : VEHICLE_HEIGHT_MIN,
    scalesLength: state.hasScales && isNum(state.scalesLength) ? state.scalesLength : undefined,
    trawls: state.trawls,
    trailers: state.trailers,
    storageAvailable: state.storageAvailable,
    storagePrice: state.storageAvailable && isNum(state.storagePrice) ? state.storagePrice : undefined,
  };
}

/**
 * Собрать черновик предложения (= тело generate/agent_assignment и общая часть create/update).
 * Возвращает null, если обязательные поля не готовы (генерация/сабмит недоступны).
 * agreeOffer в черновике — true (генерация документа не требует согласий; реальный гейт согласий —
 * в canSubmit формы).
 */
export function buildSaleDraft(state: SellFormState, address: SellAddress | null): SaleOfferDraft | null {
  if (!isOfferComplete(state, address)) {
    return null;
  }
  return {
    goodsType: state.goodsType!,
    goodsPropertiesSetId: Number(state.propsSetId),
    cropYear: state.cropYear,
    address: address!.address,
    addressTitle: address!.title,
    basisId: address!.basisId,
    userComment: state.userComment.trim() || undefined,
    price: Number(state.price),
    qualityCreditType: state.qualityCreditType!,
    specialTermsFileIds: state.extraConditions ? state.specialTermsFiles.map((f) => f.id) : [],
    loadingConditions: buildLoadingConditions(state),
    weightTon: Number(state.weightTon),
    declarationNo: state.declarationNo.trim(),
    declarationDate: isoToRu(state.declarationDate)!,
    declarationFileIds: state.declarationFiles.map((f) => f.id),
    prepayment: state.prepayment!,
    onlyByGOST: false,
    agreeOffer: true,
    toExchange: state.toExchange,
  };
}

/** Подпись значимых полей (всё, что входит в поручение, кроме комментария/согласий) — для DATA_CHANGED. */
export function signatureOf(state: SellFormState, address: SellAddress | null): string {
  const draft = buildSaleDraft(state, address);
  if (!draft) {
    return '';
  }
  const { userComment: _comment, agreeOffer: _agree, ...significant } = draft;
  return JSON.stringify(significant);
}

// ── Ключ полного дубликата (без условий погрузки/декларации/комментария) ─────────
// Базис(адрес+название) + культура + набор качества + год + объём + цена + предоплата + зачёт.
function dupKey(
  address: string,
  title: string,
  goodsType: string,
  propsSetId: number | null,
  cropYear: number | null,
  weightTon: number | null,
  price: number | null,
  prepayment: string,
  qualityCreditType: string,
): string {
  return JSON.stringify([address, title, goodsType, propsSetId, cropYear, weightTon, price, prepayment, qualityCreditType]);
}

/** Ключ дубликата текущей формы; null — обязательных полей ключа ещё нет (сравнивать не с чем). */
export function candidateDuplicateKey(state: SellFormState, address: SellAddress | null): string | null {
  if (!address || !state.goodsType || !state.propsSetId || state.weightTon === '' || state.price === '') {
    return null;
  }
  return dupKey(
    address.address,
    address.title ?? '',
    state.goodsType,
    Number(state.propsSetId),
    state.cropYear,
    Number(state.weightTon),
    Number(state.price),
    state.prepayment ?? '',
    state.qualityCreditType ?? '',
  );
}

/** Ключ дубликата существующего предложения (для сравнения с кандидатом). */
export function offerDuplicateKey(offer: SaleOrder): string {
  return dupKey(
    offer.address ?? '',
    offer.addressTitle ?? '',
    offer.goodsType ?? '',
    offer.goodsPropertiesSet?.propsSetId ?? null,
    offer.cropYear ?? null,
    offer.weightTon ?? null,
    offer.price ?? null,
    offer.prepaymentInfo?.type ?? '',
    offer.qualityCreditType ?? '',
  );
}

/** Родительская культура из класса («Пшеница 3 класс» → «Пшеница») для запроса моих предложений. */
export function parentGoodsOf(goodsClass: string, parents: readonly string[]): string {
  return parents.find((p) => goodsClass === p || goodsClass.startsWith(`${p} `)) ?? goodsClass.split(' ')[0]!;
}

/** Префилл состояния из существующего предложения (edit). */
export function stateFromOffer(offer: SaleOrder): SellFormState {
  const init = initialSellForm();
  const lc = offer.loadingConditions;
  return {
    ...init,
    goodsType: offer.goodsType ?? null,
    propsSetId: offer.goodsPropertiesSet?.propsSetId != null ? String(offer.goodsPropertiesSet.propsSetId) : null,
    cropYear: offer.cropYear ?? init.cropYear,
    weightTon: offer.weightTon ?? '',
    toExchange: offer.toExchange ?? false,
    price: offer.price ?? '',
    prepayment: offer.prepaymentInfo?.type ?? init.prepayment,
    qualityCreditType: offer.qualityCreditType ?? init.qualityCreditType,
    startMode: (lc?.startDateMode as StartDateMode) ?? 'NO_DATE',
    startDate: ruToIso(lc?.startDate) ?? '',
    endDate: ruToIso(lc?.endDate) ?? '',
    startTime: lc?.startTime ?? init.startTime,
    endTime: lc?.endTime ?? init.endTime,
    shipmentModes: lc?.shipmentModes ?? [],
    trailers: lc?.trailers ?? true,
    trawls: lc?.trawls ?? true,
    hasScales: lc?.scalesLength != null,
    scalesLength: lc?.scalesLength ?? '',
    vehicleHeight: lc?.maxVehicleHight ?? init.vehicleHeight,
    rateType: (lc?.dailyShippingRate?.type as RateType) ?? 'ton',
    rate: lc?.dailyShippingRate?.value ?? '',
    storageAvailable: lc?.storageAvailable ?? false,
    storagePrice: lc?.storagePrice ?? '',
    declarationNo: offer.declarationNo ?? '',
    declarationDate: ruToIso(offer.declarationDate) ?? '',
    declarationFiles: offer.declarationFiles ?? [],
    extraConditions: (offer.specialTermsFiles?.length ?? 0) > 0,
    specialTermsFiles: offer.specialTermsFiles ?? [],
    userComment: offer.userComment ?? '',
  };
}
